import { useEffect, useRef, useState } from 'react';
import { stampa } from '../../api/stampa';
import type { DizionarioStampa, ModelloLetto } from '../../api/stampa';

/*  Il modello Word e l'elenco di cosa ci si può scrivere dentro.

    Stanno nella stessa schermata dei testi perché sono la stessa responsabilità: le
    parole che escono su un'offerta. Chi corregge una clausola è chi sposta il logo.

    Il dizionario è la parte che si guarda più spesso, ed è l'unica documentazione che
    esiste: i nomi escono dallo stesso codice che produce i valori, quindi non possono
    raccontare qualcosa di diverso. Scritti in un README, divergerebbero alla prima
    aggiunta e chi scrive il modello si ritroverebbe a battere un nome che non esiste più.

    Le versioni non si cancellano. Caricarne una nuova mette da parte quella di prima: se
    il logo esce storto si torna indietro con un clic, invece di cercare il file giusto
    nella posta di qualcuno.
*/
export function ModelloEDizionario({ modificabile }: { modificabile: boolean }) {
  const [modelli, setModelli] = useState<ModelloLetto[] | null>(null);
  const [dizionario, setDizionario] = useState<DizionarioStampa | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [dizionarioAperto, setDizionarioAperto] = useState(false);
  const [nota, setNota] = useState('');

  const scelta = useRef<HTMLInputElement>(null);

  const ricarica = () => {
    stampa
      .modelli()
      .then(setModelli)
      .catch((e: Error) => setErrore(e.message));
  };

  useEffect(() => {
    ricarica();
    stampa
      .dizionario()
      .then(setDizionario)
      .catch((e: Error) => setErrore(e.message));
  }, []);

  const esegui = (promessa: Promise<unknown>) => {
    setInCorso(true);
    setErrore(null);
    promessa
      .then(() => {
        setNota('');
        ricarica();
      })
      .catch((e: Error) => setErrore(e.message))
      .finally(() => setInCorso(false));
  };

  const attivo = modelli?.find((m) => m.attivo);

  return (
    <section className="modello-stampa">
      <h2>Modello dell'offerta</h2>
      <p className="nota">
        Il documento Word con la carta intestata e i segnaposto. Si scarica, si corregge in
        Word e si ricarica: la versione di prima resta, spenta, e si può rimettere.
      </p>

      {errore !== null && <p className="errore">{errore}</p>}

      {modelli !== null && modelli.length === 0 && (
        <p className="nota">
          Nessun modello caricato: finché non ce n'è uno, la stampa dell'offerta non parte.
        </p>
      )}

      {attivo !== undefined && (
        <p className="attivo">
          In uso: <strong>{attivo.nomeFile}</strong> — versione {attivo.versione}, caricata da{' '}
          {attivo.caricatoDa}
        </p>
      )}

      {modificabile && (
        <div className="caricamento">
          <input
            ref={scelta}
            type="file"
            accept=".docx"
            hidden
            onChange={(evento) => {
              const file = evento.target.files?.[0];
              // Il campo si azzera: lo stesso file ricaricato due volte di fila non fa
              // scattare `change` se il valore non cambia.
              evento.target.value = '';
              if (file) esegui(stampa.caricaModello(file, nota || undefined));
            }}
          />
          <input
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="Cosa cambia in questa versione"
            maxLength={500}
          />
          <button type="button" disabled={inCorso} onClick={() => scelta.current?.click()}>
            {inCorso ? 'Caricamento…' : 'Carica una versione'}
          </button>
        </div>
      )}

      {modelli !== null && modelli.length > 0 && (
        <table className="versioni">
          <thead>
            <tr>
              <th>Ver.</th>
              <th>File</th>
              <th>Cosa cambia</th>
              <th>Chi</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {modelli.map((m) => (
              <tr key={m.id} className={m.attivo ? 'in-uso' : ''}>
                <td>{m.versione}</td>
                <td>{m.nomeFile}</td>
                <td>{m.nota ?? ''}</td>
                <td>{m.caricatoDa}</td>
                <td className="comandi">
                  <button type="button" onClick={() => void stampa.scaricaModello(m.id)}>
                    Scarica
                  </button>
                  {modificabile && !m.attivo && (
                    <button
                      type="button"
                      disabled={inCorso}
                      onClick={() => esegui(stampa.attivaModello(m.id))}
                    >
                      Rimetti in uso
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3>
        <button
          type="button"
          className="collegamento"
          onClick={() => setDizionarioAperto((aperto) => !aperto)}
        >
          {dizionarioAperto ? 'Nascondi' : 'Mostra'} i segnaposto disponibili
        </button>
      </h3>

      {dizionarioAperto && dizionario !== null && (
        <div className="dizionario">
          <p className="nota">
            Si scrivono nel documento fra cancelletti — <code>#preventivo.numero#</code> — e
            valgono anche in intestazione e piè di pagina. Un blocco che si stampa solo a
            certe condizioni si apre con <code>#se prezzi#</code> e si chiude con{' '}
            <code>#fine#</code>.
          </p>

          <h4>Campi</h4>
          <dl>
            {dizionario.campi.map((c) => (
              <div key={c.nome}>
                <dt>#{c.nome}#</dt>
                <dd>{c.cosa}</dd>
              </div>
            ))}
          </dl>

          <h4>Elenchi</h4>
          <p className="nota">
            Una riga di tabella che contiene questi segnaposto viene ripetuta per ogni
            voce. Mettendone una per tipo, ognuna esce con la sua formattazione.
          </p>
          {dizionario.elenchi.map((e) => (
            <div key={e.nome} className="elenco-dizionario">
              <h5>{e.nome}</h5>
              <p className="nota">{e.cosa}</p>
              <dl>
                {e.colonne.map((c) => (
                  <div key={c.nome}>
                    <dt>#{c.nome}#</dt>
                    <dd>{c.cosa}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}

          <h4>Condizioni</h4>
          <dl>
            {dizionario.condizioni.map((c) => (
              <div key={c.nome}>
                <dt>#se {c.nome}# … #fine#</dt>
                <dd>{c.cosa}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </section>
  );
}
